import {DOM, ENodeType} from "lib/commons/xml/dom";
import {IJmlNode, IJmlObj, IJmlSet, JML} from "lib/commons/xml/jml";
import {IXAddr, IXAddrRange, XA} from "lib/commons/xml/xAddr";
import {IXmlMsg, replaceXmlContent, XmlBatch, XmlBodyState, XmlDeleteMsg, XmlInsertMsg, XmlListMsgOt, XmlStrMsg} from "lib/edit/ot/xmlHouse";
import {EPastePos, IImportPos, ISkCrossDomMgr, OSkExecInternalOptions, SchemaDom, SkContext, SkNode} from "lib/edit/schema/schema";
import {EAnnotLevel, ISkStructDef, SkAnnotEltFree, SkAnnotWrongValue} from "lib/edit/schema/schemaAnnots";
import {ISkRuleObj, SkRule, SkRuleAttr, SkRuleChoice, SkRuleElt, SkRuleNode} from "lib/edit/schema/schemaPatterns";
import {MsgOt} from "lib/edit/ot/houseOt";
import {CONVERT, IConvertSrc} from "lib/edit/import/convert";
import {SK_NS} from "lib/edit/schema/schemaBuilder";
import {INodeRulePointer} from "lib/edit/ot/xmlTypedHouse";
import {IGram, ISkMetaAsList} from "lib/edit/import/gram";
import {IXmlIdMgr} from "lib/edit/schema/xmlIdMgr";
import IS_comment = DOM.IS_comment;

/** Définition d'un méta-modèle. */
export class SkMetaLib {
	protected _dict: Dict<ISkMetaNode>;

	constructor(parent?: SkMetaLib) {
		this._dict = Object.create(parent ? parent._dict : null);
	}

	registerMetaNode(skm: ISkMetaNode) {
		this._dict[skm.id] = skm;
	}

	getMetaNode(id: string): ISkMetaNode {
		const m = this._dict[id];
		if (!m) throw Error(`SkMetaModel not found : ${id}`);
		return m;
	}
}

/** Méta-modèle par défaut. */
export const SKMETALIB = new SkMetaLib();

/**
 * Méta modèle d'un noeud associé à un SkRule.
 * Les types de noeuds Document, Element, Attribute, Comment sont associés à un skMeta.
 * Seuls les noeuds textes ne sont pas associés à un skMeta : leur meta-modèle est défini par leur container.
 */
export interface ISkMetaNode {
	id: string

	gramLevelType: EGramLevelType

	/**
	 * Extrait les configurations propres à ce SkMetaNode (propriétés supp., règles de transformations...)
	 * et les mémorise sur le skRule.
	 */
	initSkRule(skRule: SkRule, confRule: Element): void

	/**
	 * Règle d'identification supplémentaire du SkRule (attributs fixes).
	 */
	ruleMatch(skRule: SkRule, node: Node | IJmlNode): boolean

	/**
	 * Exporte un noeud DOM avec tout ou partie de ses fils.
	 * La cible de l'export peut-être un JML ou un autre noeud (fragemnt) du même document que la source.
	 * @param src SkNode à exporter
	 * @param parentTarget Container de la copie. Peut-etre null pour n'exploiter que l'algo de delete.
	 * @param scope range de l'export et config d'actions complémentaires comme l'enrichissement
	 *    d'un batch supprimant le contenu exporté.
	 * @param fromDepth Si non null, scope.start[fromDepth] correspond au 1er fils à exporter.
	 * @param toDepth Si non null, scope.end[toDepth] correspond au dernier fils à partiellement exporter
	 *      ou à l'interstice de fin de la copie si toDepth est le dernier élément de scope.end.
	 * @return parentTarget dans lequel le contenu cloné a été ajouté.
	 */
	exportNode(src: SkNode, parentTarget: Node | IJmlSet | null, scope?: OSkMCopyScope, fromDepth?: number | null, toDepth?: number | null): Node | IJmlSet | null

	/**
	 * Exporte un noeud avec des filtres spéciaux propres au schéma (ex: cells de tables...)
	 */
	exportNodeCustom(src: SkNode, filters: Jsonisable): Node

	/**
	 * Exporte au format texte.
	 */
	exportAsText(node: Node, rule: SkRule, buf: string[]): void

	/**
	 * Exporte au format HTML.
	 */
	exportAsHtml(node: Node, rule: SkRule, outParent: HTMLElement): void

	/**
	 * Surcharge de la création d'un noeud à partir d'un SkRule.
	 */
	onCreateNode?(rule: SkRuleNode, dst: Node): void;

	/**
	 * Retourne les différentes possibilités d'import d'un contenu dans le contexte donné.
	 */
	tryPasteNodes(ctx: OSkImportCtxInternal, content: Node, cache: OSkImportDatas): Promise<ISkImporter[]>

	tryPasteText(ctx: OSkImportCtxInternal, text: string, cache: OSkImportDatas): Promise<ISkImporter[]>

	tryPasteLinks<L>(ctx: OSkImportCtxInternal, linksInfo: L, cache: OSkImportDatas): Promise<ISkImporter[]>

	tryImportNodes(ctx: OSkImportCtxInternal, content: Node, cache: OSkImportDatas): Promise<ISkImporter[]>

	tryImportLinks<L>(ctx: OSkImportCtxInternal, linksInfo: L, cache: OSkImportDatas): Promise<ISkImporter[] | null> | null

	/** Méthode interne utilisée par tryPasteLinks() et tryImportLinks() pour trouver parmi les fils une struct susceptible d'accepter un lien. */
	_pushLinksImporters<L>(results: ISkImporter[], skRule: ISkRuleObj, pos: EPastePos | IImportPos, ctx: OSkImportCtxInternal, linksInfo: L, cache: OSkImportDatas): void;

	/**
	 * Retourne les différentes possibilités de transformation d'un noeud.
	 * note: transformations déclarées sur le skRuleNode du node + peut remonter au père pour trouver des permutes de 'sm:part' possibles.
	 *
	 */
	//tryTransform(...): Promise<any/*ISkTransformer*/[]>

	//Liste les export spéciaux possibles (dans d'autres formats).
	//todo tryExport(node: SkNode, results: ISkExporter[]):number;

	/**
	 * Normalise les caractères (white-spaces en particulier) d'un noeud texte ou d'un attribut.
	 *
	 * @param noTrim Si le noeud n'est pas dans son contexte (noeud précédent / suivant), le trim au bord des paragraphes est impossible.
	 * @param val Valeur du noeud texte à prendre en compte à la place de ctn.nodeValue dû à un merge précédent des noeuds textes.
	 */
	normChars(ctn: Text | Attr, msgs: MsgOt[], noTrim?: boolean, val?: string): void

	/**
	 * Appelé uniquement pour les élements.
	 */
	onExecRulesStart(skCtx: SkContext): void

	onExecRules(skCtx: SkContext, rule: ISkRuleObj, nodeOrAttr: Node | Attr): void

	/**
	 * Surcharge la liste des structures du schéma susceptibles de remplacer ce noeud dans ce contexte.
	 * Méthode appelée sur le ISkMetaNode de l'élément parent.
	 *
	 * @param skAnc Noeud existant ancêtre du document dans lequel on recherche une alternative .
	 * @param parent Contexte parent direct de la recherche d'alternatives.
	 * @param toReplace Si trouvé, rule qui doit être remplacé.
	 * @param offset Offset d'insertion prévu des alternatives.
	 * @param nodeType Type du noeud/attribut dont on cherche les alternatives (utile si toReplace non trouvé).
	 * @param nodeName Nom du noeud/attribut dont on cherche les alternatives (utile si toReplace non trouvé).
	 */
	overwriteAlternates(skAnc: SkNode, parent: SkRuleNode, toReplace: ISkRuleObj, offset: number, nodeType: ENodeType, nodeName: string, result: ISkStructDef[]): ISkStructDef[];

	/** Cette structure peut-elle être listée dans les éléments insérables. */
	isInsertable(parent: SkNode, rule: ISkRuleObj): boolean

	/** Cet élément, attribute ou commentaire peut-il etre parcouru pour une recherche textuelle (voir un repmplacement). */
	isTextSearchable(skNode: SkNode, nodeOrAttr: Node | Attr): boolean

	/** */
	computeGramBindingMalus?(gram: IGram, rule: ISkRuleObj, childrenMalus: number): number;

	/**
	 * Retourne un aspect lié à ce noeud (élément ou attribut).
	 * @param rule Rule associé à ce skMeta.
	 * @param skNode élément d'ancrage de cet aspect qui peut être un ancêtre dans le cas d'un aspect sur un élement ou un attribut virtuel.
	 */
	getAspect<A>(keyAspect: string, rule: ISkRuleObj, skNode: SkNode, options: any): A;
}


/** Niveau grammatical du skRule de type Element (cf Api Gram). */
export enum EGramLevelType {
	/** Block (valeur par défaut pour les SkMGenericElt). */
	block = 1,

	metas = 2,
	text = 3,
	inline = 4,

	/** SkRules comments ou attributes */
	undef = 0
}

/** Options pour la copie ou l'export. */
export interface OSkMCopyScope extends IXAddrRange {

	/**
	 * Si renseigné, les msg de suppression corespondant au range (et pas à ce qui est copié) sont ajoutés à ce batch.
	 * Ne pas utiliser pour les fonctions de cut/suppression de la sélection (ie sans être suivi d'une insertion de noeuds au point d'insertion)
	 * car ne gère pas la fusion des 2 noeuds textes consécutifs qui pourrait résulter de la suppression du range.
	 */
	deletes?: XmlBatch

	/**
	 * Si deletes est renseigné, force le split du noeud texte qui contient le range supprimé.
	 * Utilisé pour les actions de wrapping : garanti la présence d'un point d'insertion après le noeud texte de this.start (ie
	 * split le texte si le range est interne à un noeud texte).
	 */
	forceSplitText?: boolean

}

/**
 * Contexte d'un import de contenu par une action "Coller ici", ie à la place ou autour d'une sélection.
 */
export interface OSkPasteContext {

	/**
	 * Range (qui sera supprimé à l'exécution de l'import) ou point d'insertion définit par sel.start.
	 * Si sel est un range non collapsé, sel.start et sel.end doivent pointer des gap non virtuels.
	 * Si sel est collapsé, sel.start peut intégrer des fragments virtuels qui seront compensés avec
	 * la propriété virtualPath.
	 */
	sel: IXAddrRange

	/**
	 * Derniers fragments de sel.start qui sont virtuels avant le point d'insertion final.
	 * Doit être null si sel est un range non collapsé.
	 * Chaque fragment peut-être :
	 * - IJmlObj : élément ou commentaire
	 * - '#' : noeud texte (dernier fragment de virtualPath)
	 * - '@' : attribut (dernier fragment de virtualPath), le nom de l'attribut est à l'avant dernier item de sel.start
	 *
	 * Exemples :
	 * sel.start  | noeuds virtuels   | virtualPath
	 *            | / pt insert final |
	 * 0/3/2/0       0                  null
	 * 0/3/2/0       2/0                [{'':'tag'}]
	 * 0/3/2/0       2/0                ['#']
	 * 0/3/2/0       3/2/0              [{'':'parentTag'}, {'':'tag'}]
	 * 0/3/2/0       3/2/0              [{'':'tag'}, '#']
	 * 0/3/2/0       3/2/0              [{'':'tag'}, {'':'!'}]
	 * 0/3/2/att     att                null
	 * 0/3/2/att     2/att              [{'':'tag'}]
	 * 0/3/2/att/0   0                  null
	 * 0/3/2/att/0   att/0              ['@']
	 * 0/3/2/att/0   2/att/0            [{'':'tag'}, '@']
	 */
	virtualPath?: Array<IJmlObj | '#' | '@'>

	/** Coller brut, préserve les structures inconnues (éditeur XML générique). */
	preserveUnknown?: boolean

	/**
	 * Indique de ne rien tenter si le contenu ne s'avère pas de l'inline.
	 * Une deuxième tentative dans un contexte texte (paraSibling) sera alors tentée.
	 */
	abortIfNotInline?: boolean

	/**
	 * Fournit une restriction sur les traitements possibles pour obtenir la le contenu cible à importer.
	 * undefined : tout traitement possible parsing xml, html puis markdown
	 * markdown : interprête la source comme du mardkown (ie pas de xml ou html)
	 * text : traite les saut de ligne comme des paragraphes (ie pas de markdown, xml ou html)
	 * string : aucun traitement, le texte est pris telquel
	 */
	targetHint?: "string" | "text" | "markdown" | undefined

	/**
	 * TODO Coller demandé avec écran d'options avancées possible (fonction "Coller avancé/special"),
	 * 	les options possibles étant définies et affichées après analyse de la source.
	 */
	withOptions?: boolean
}

/**
 * Contexte d'un import de contenu par une action "Coller quelque part...", ie une insertion ou un remplacement
 * quelque part dans les fils (noeud ou attributs) d'un contexte parent donné.
 */
export interface OSkPasteSearchContext {

	/**
	 * Parent à partir duquel chercher les points d'insertion/remplacement possibles.
	 * Correspond à XA.OSkPasteContext.sel.start MOINS le last fragment correspondant au point d'insertion.
	 */
	xaParent: IXAddr

	/**
	 * Derniers fragments de xaParent qui sont virtuels.
	 * Même défition que OSkPasteContext.virtualPath, en 'absence ici du point d'insertion final.
	 *
	 * NOTE : '#' jamais utilisé (définit ici pour compat de type TS avec OSkPasteContext).
	 */
	virtualPath?: Array<IJmlObj | '#' | '@'>

	/** TODO Import demandé avec écran d'options avancées possible (fonction "Coller avancé/special"). */
	withOptions?: boolean
}


/** Au fil des transformations peut mémoriser des données pour réexploitations par d'autres tentatives. */
export interface OSkImportDatas extends Dict<any> {

	/** Mémoire du DOM original si existe (issu du presse-papier) pour envisager d'autres tentatives. */
	originalDom?: Node

	/** Mémoire du Texte original si existe (issu du presse-papier) pour envisager d'autres tentatives. */
	originalText?: string

	/** Mémoire du Link original si existe (issu du presse-papier) pour envisager d'autres tentatives. */
	originalLinks?: Jsonisable

	/** Mémoire du DOM original si existe (issu du presse-papier) au format HTML */
	originalHtml?: Node

	/** Transposition en Gram. */
	gram?: IGram

	/** Indique qu'une recherche d'insertion dans la descendance a déjà été tentée. */
	deepFindDone?: boolean

	/**
	 * IDEE : pour les tryImport(), essaye cette alternative dégradée si la première ne trouve pas de solution.
	 * Ex. d'usage : part d'une compoPrim, sinon contenu de la part/lien.
	 */
	//alternateDatas?: OSkImportDatas
}

/** Mémoires communes de OSkPasteContext et OSkPasteSearchContext utilisés en interne. */
export interface OSkImportCtxInternal extends OSkPasteContext, OSkPasteSearchContext {

	schemaDom: SchemaDom

	/**
	 * Container du point d'insertion réel (document, élément, attr, text, comment),
	 * ie, container de la "séquence de départ" tel que définit dans ISkImporter.pastePos.
	 */
	ctn: Node

	/** Profondeur dans le IXAddr de OSkPasteContext.sel.start/OSkPasteSearchContext.xaParent correspondant à l'insertion réelle, dans this.ctn */
	insertDepth: number

	/**
	 * SkNode contexte (ancrage réel sur un noeud Document ou Element).
	 * Correspond au skNode de this.ctn si c'est un élément, au contexte parent si c'est un attribut ou un noeud texte ou commentaire.
	 */
	skNode?: SkNode

	/**
	 * Stack des SkRule à partir de l'ancrage réél de this.skNode.
	 * Existe si :
	 * - le container de l'insertion est un attribut ou un commentaire
	 * - ou si l'insertion est dans un virtualPath.
	 *
	 * ATTENTION : Actuellement systématiquement calculé à l'appel de ISkMetaNode.tryXxx(),
	 * pas dans ISkImporter.doImport(). Appeler SKMETA.ctxLastSkRule(ctx) pour disposer
	 * de ctx.skRuleStack.
	 */
	skRuleStack?: SkRule[]
}


/** Possibilité d'import ou de transformation. */
export interface ISkImporter {

	/** Libellé de cet import affiché à l'utilisateur lorsque plusieurs possibilités sont offertes. */
	getLabel(): string

	/** Imperfections/partialité de l'import. 0 = import à priori complet et parfait. */
	malus: number;

	/**
	 * Utilisé dans le cadre de OSkPasteContext ("Coller ici").
	 *
	 * Cibles possibles de l'action de coller par rapport à la "séquence de départ" de OSkPasteContext.sel.
	 * Concept de "séquence de départ" : la séquence de départ d'une sélection contient tous
	 * les noeuds inclus dans la sélection appartenant à la même séquence de noeuds que le point de départ;
	 * le point terminal d'une sélection peut ne pas se trouver dans la même séquence que le point de départ.
	 *
	 * Si non renseigné, le ISkImporter ne gère que le remplacement
	 * de la sélection courante ou une opération plus complexe d'import interne partiel (cellules de tableau,
	 * remplacement d'un lien...)
	 */
	pastePos?: EPastePos | EPastePos

	/**
	 * Utilisé dans le cadre de OSkPasteSearchContext ("Coller quelque-part").
	 *
	 * Cibles possibles d'insertion ou remplacement d'une action "Coller quelque-part" en fils de OSkPasteSearchContext.xaParent
	 * Si xaParent est un attribut et que importPos est null, c'est toute la valeur de l'attribut qui est remplacé (lien).
	 *
	 * Attention : ces données doivent être utilisées dès la réception des imports possibles, et sont obsolets aprés
	 * un traitement asynchrone (en cas d'édition concurrentes).
	 */
	importPos?: IImportPos

	/**
	 * Complète les données pour l'import lors du 'drop' (les files ne sont pas dispo en dehors de cette phase de drop).
	 * Etape appelée avant buildContentToImport() et doImport().
	 * Retourne 'stop' si après récupération de ces datas précisant les types possibles, on constate
	 * que ce ISkImporter n'est plus possible.
	 */
	completeDatasAtDrop?(dataTransfer: DataTransfer): Promise<void | 'stop'>

	/**
	 * Cet importer exige-t-il une construction asynchrone ? Exemples : l'import d'un
	 * lien pourrait proposer l'import de son contenu à fetcher en async.
	 */
	needAsyncBuild?: boolean

	/**
	 * Construit le contenu à importer.
	 * Retour en promise car peut exiger un chargement async d'une lib de transformation (xsl..)
	 * ou un contenu distant (internalisation du contenu...)
	 *
	 * Retourne 'stop' si la construction a échoué ou été annulée.
	 */
	buildContentToImport?(uiContext?: HTMLElement): Promise<void | 'stop'>

	/**
	 * Ajoute les évènements au batch pour effectuer l'action. A ce stade, il ne doit plus y avoir
	 * d'action asynchrone qui pourrait rendre invalide le contexte.
	 *
	 * Si needAsyncBuild == true :
	 * - doImport() ne peut être appelé qu'après await buildContentToImport().
	 * - Un OSkPasteContext mis à jour (par rapport à celui passé dans ISkMetaNode.tryXxx()) doit être passé
	 *   en paramètre en cas de modification concurrente (pendant buildContentToImport).
	 *
	 * Si cet importer est issu d'un SchemaDom.tryImport(context: OSkPasteSearchContext), ie un "coller quelquepart",
	 * le contexte appelant doit instancier un nouveau OSkPasteContext spécifiant l'emplacement finalement choisi.
	 *
	 * @param options TODO Options spécifiques à chaque impl de ISkImporter. cf OSkPasteContext.withOptions
	 */
	doImport(context: OSkPasteContext, batch: XmlBatch, options?: any): void

}

function cleanupNodes(node: Node, ctx: OSkImportCtxInternal, startRule: SkRuleNode, malus: number, parentRule: SkRuleNode): { node: Node; malus: number } {
	if (node instanceof Element) {
		let subSchemaDom = new SchemaDom(ctx.schemaDom.schema, startRule, node);
		let cycles = 0;
		let results = subSchemaDom.validateDocument({autoMutate: true, autoComplete: true, autoCleanup: true, autoNormXml: true, autoNormChars: true, importCtx: ctx});
		if (results.mutations || results.corrections.length > 0) node = node.cloneNode(true); //on clone avant corrections.
		while (results.mutations || results.corrections.length > 0) {
			//PERF mémoire des deltas et revalidaton des zones modifiées comme dans XmlTypedState.update() ?
			//On execute les muatations seules avant les autres corrections.
			if (!results.mutations) malus += results.corrections.length * 10; //TODO à affiner : ne compter que les length des Msg de type delete ?
			new XmlBodyState('').resetDom(node).update(new XmlListMsgOt().initConcurrentList(results.mutations || results.corrections));
			const subRule = parentRule.contentRule.findRuleNodeFor(node);
			subSchemaDom = new SchemaDom(ctx.schemaDom.schema, subRule, node as Element);
			results = subSchemaDom.validateDocument({autoMutate: true, autoComplete: true, autoCleanup: true, autoNormXml: true, autoNormChars: true, importCtx: ctx});
			if (cycles++ > 50) {
				console.warn("Infinite validation loop: ", results, node);
				break;
			}
		}
		//TODO rewrite NS prefix de notre schéma.
	} else if (node instanceof Text) {
		//trim des white-spaces : on passe par le skMeta en prévision de para de type "pre" (préservation des espaces, tab, retour charriot...)
		const msgs = [] as IXmlMsg[];
		parentRule.skMeta.normChars(node, msgs, true);
		if (msgs.length > 0) {
			node = node.cloneNode(true); //on clone avant corrections.
			new XmlBodyState('').resetDom(node).update(new XmlListMsgOt().initConcurrentList(msgs));
		}
	} else {
		console.error("Node type unknown", node);
	}
	return {node, malus};
}

export abstract class SkMBase implements ISkMetaNode {

	get gramLevelType(): EGramLevelType {return EGramLevelType.undef}

	constructor(readonly id: string) {}

	initSkRule(skRule: SkRule, confRule: Element): void {}

	ruleMatch(skRule: SkRule, node: Node | IJmlObj): boolean {return true}

	exportNode(src: SkNode, parentTarget: Node | IJmlSet | null, scope?: OSkMCopyScope, fromDepth?: number | null, toDepth?: number | null): Node | IJmlSet {
		if (fromDepth == null && toDepth == null) {
			//on prend tout le noeud
			if (parentTarget) JML.appendDomNode(src.node, true, parentTarget);
			//if (scope.deletes) scope.deletes.add(new XmlDeleteMsg().init(XA.from(src.node), 1)); non delete par l'appelant
		} else {
			let from = fromDepth != null ? scope.start[fromDepth] || 0 : 0;
			if (typeof from === 'string') throw Error(`Export fragment throw attributes not allowed: ${scope.start}`);
			let clone = this.exportContainerNode(src, parentTarget, scope, fromDepth, toDepth);
			if (clone) {
				//on saute les fils déjà copiés par this.exportContainerNode()
				from = Math.max(from, Array.isArray(clone) ? JML.lengthJmlOrText(clone) : clone.childNodes.length);
				//décalage des metas comptabilisés, on arrête la copie inutile.
				if (!parentTarget) clone = null;
			} else {
				//pas de container copié, on ajoute dans parentTarget.
				clone = parentTarget;
			}
			const to = toDepth != null && toDepth < scope.end.length ? scope.end[toDepth] : Infinity;
			if (typeof to === 'string') throw Error(`Export fragment throw attributes not allowed: ${scope.end}`);

			let ch = from === 0 ? src.node.firstChild : src.node.childNodes.item(from);
			let i = from;
			//copie partielle de from.
			if (ch && i < to && fromDepth != null && fromDepth < scope.start.length - 1) {
				const subSkNode = src.schemaDom.getSkNode(ch);
				if (subSkNode) {
					subSkNode.rule.skMeta.exportNode(subSkNode, clone, scope, fromDepth + 1, null);
				} else {
					if (clone) XA.cloneAfter(ch, clone, scope.start, fromDepth + 1);
					if (scope.deletes) {
						if (!(ch instanceof CharacterData)) throw Error();
						const startOffset = XA.last(scope.start);
						if (typeof startOffset === 'string') throw Error();
						if (startOffset < ch.length) scope.deletes.add(new XmlDeleteMsg().init(scope.start, ch.length - startOffset));
					}
				}
				ch = ch.nextSibling;
				i++;
				from++; //pour scope.deletes
			}
			//copie complète des noeuds après from et avant to.
			//let last = toDepth === scope.end.length - 1 ? to - 1 : to;
			while (ch && i < to) {
				if (clone) JML.appendDomNode(ch, true, clone);
				ch = ch.nextSibling;
				i++;
			}
			//copie partielle de to.
			if (ch && toDepth != null && toDepth < scope.end.length - 1) {
				//on est dans la hiérarchie de scope.end
				const subSkNode = src.schemaDom.getSkNode(ch);
				if (subSkNode) {
					subSkNode.rule.skMeta.exportNode(subSkNode, clone, scope, null, toDepth + 1);
				} else {
					if (clone) XA.cloneBefore(ch, clone, scope.end, toDepth + 1);
					if (scope.deletes) {
						if (!(ch instanceof CharacterData)) throw Error();
						const endOffset = XA.last(scope.end);
						if (typeof endOffset === 'string') throw Error();
						if (endOffset > 0) scope.deletes.add(new XmlDeleteMsg().init(XA.newBd(scope.end).setAtDepth(-1, 0).xa, endOffset));
					}
				}
			}
			if (scope.deletes && i > from) scope.deletes.add(new XmlDeleteMsg().init(XA.append(XA.from(src.node), from), i - from));
		}
		return parentTarget;
	}

	exportNodeCustom(src: SkNode, filters: Jsonisable): Node {
		return src.node.cloneNode(true);
	}

	exportAsText(node: Node, rule: SkRule, buf: string[]): void {
		for (let ch = node.firstChild; ch; ch = ch.nextSibling) {
			if (ch instanceof Text) buf.push(ch.nodeValue);
			else {
				const r = (ch as INodeRulePointer).rule;
				if (r && r.skMeta) r.skMeta.exportAsText(ch, r, buf);
			}
		}
	}

	exportAsHtml(node: Node, rule: SkRule, outParent: HTMLElement): void {
		for (let ch = node.firstChild; ch; ch = ch.nextSibling) {
			if (ch instanceof Text) outParent.appendChild(outParent.ownerDocument.createTextNode(ch.nodeValue));
			else {
				const r = (ch as INodeRulePointer).rule;
				if (r && r.skMeta) r.skMeta.exportAsHtml(ch, r, outParent);
			}
		}
	}

	protected exportAsHtmlChildren(node: Node, rule: SkRule, outParent: HTMLElement): void {
		SkMBase.prototype.exportAsHtml.call(this, node, rule, outParent);
	}


	/** Exporte le container "logique" de ce noeud (sans son contenu) en fonction du scope et des particularités du schéma. */
	exportContainerNode(src: SkNode, parentTarget: Node | IJmlSet | null, scope?: OSkMCopyScope, fromDepth?: number | null, toDepth?: number | null): Node | IJmlSet {
		if (fromDepth != null && toDepth != null) return null;//copy d'un fragment du contenu => pas de container
		return parentTarget ? JML.appendDomNode(src.node, false, parentTarget) : null;
	}

	/** N'exporte que le contenu "logique" de ce noeud, sans son container "logique". Impl de base : export tous les noeuds fils. */
	exportContent(src: SkNode, parentTarget: Node | IJmlSet) {
		let ch = src.node.firstChild;
		while (ch) {
			JML.appendDomNode(ch, true, parentTarget);
			ch = ch.nextSibling;
		}
	}

	async tryPasteNodes(ctx: OSkImportCtxInternal, content: Node, cache: OSkImportDatas): Promise<ISkImporter[]> {
		const known = await this.findKnownNodes(ctx, content, true, cache);
		if (known) {
			if (known.nodes[0] instanceof Attr) {
				//TODO import attrs...
				console.log("TODO import attrs...");
			} else {
				let importPos = EPastePos.none;
				let malus = known.malus;
				let nodes: Node[];
				if (ctx.skRuleStack) {
					//On est dans un contexte virtuel, on vérifie l'offset de la 1ère rule virtuelle.
					const offset = ctx.sel.start[ctx.sel.start.length - 1 - ctx.virtualPath.length] as number;
					nodes = known.nodes;
					importPos = ctx.schemaDom.getPastePos(ctx.ctn, offset, offset, ctx.skRuleStack[0]);
				} else {
					const startSeq = XA.range2StartSeq(ctx.sel);
					const offsetMin = XA.last(startSeq.start) as number;
					const offsetMax = offsetMin + startSeq.len;
					nodes = [];
					for (const node of known.nodes) {
						if (ctx.ctn instanceof Text) {
							importPos |= EPastePos.anywhere;
							nodes.push(node);
						} else if (node instanceof Comment) {
							importPos |= EPastePos.anywhere;
							nodes.push(node);
						} else {
							const pos = ctx.schemaDom.getPastePos(ctx.ctn, offsetMin, offsetMax, node);
							if (pos !== EPastePos.none) {
								importPos |= pos;
								nodes.push(node);
							} else {
								malus += 10;
							}
						}
					}
				}
				if (importPos !== EPastePos.none) return [new SkImpSimple(ctx, nodes, malus, importPos)];
			}
		}
		return null;
	}

	/**
	 *
	 * @param ctx
	 * @param content Contenu à importer
	 * @param deepFind Si true, et que le noeud racine est inconnu, saute ce noeud et retourne les fils si ils sont connus dans ce contexte.
	 * @param cache
	 */
	async findKnownNodes(ctx: OSkImportCtxInternal, content: Node, deepFind: boolean, cache: OSkImportDatas): Promise<{ nodes: Node[] | Attr[], malus: number } | null> {
		if (content.localName === 'fragment' && content.namespaceURI === DOM.SCCORE_NS) {
			if (typeof SKMETA.ctxInsertStartOffset(ctx) === 'string') return null;
			//TODO dtd sc:attributes pour copie d'un à n attributs
			//Problème : ctx est sur le container de la cible, il faudrait que ctx pointe directement l'élément cible pour évaluer le collage d'attributs.
			//Détecter qu'on colle des attr en amont et construire le ctx en conséquence ?
			//const atts = DOM.findFirstChild(content, (ch: Node): ch is Element => ch.nodeName === SKMETA.SCATTRIBUTES_TAG && ch.namespaceURI === DOM.SCCORE_NS);
			// if (atts) {
			// 	//Copie d'attributs
			// 	if (ctx.preserveUnknown) {
			// 		return content.hasChildNodes() ? {nodes: Array.from(atts.attributes), malus: 0} : null;
			// 	} else {
			// 		const attList = atts.attributes;
			// 		const nodes: Node[] = [];
			// 		let malus = 0;
			// 		for (let i = 0; i < attList.length; i++) {
			// 			const att = attList.item(i);
			// 			const knownNode = await this.mutateNodeToKnownNode(ctx, att, cache);
			// 			if (knownNode) {
			// 				malus += knownNode.malus;
			// 				if (knownNode.node instanceof DocumentFragment) {
			// 					for (let n = knownNode.node.firstChild; n; n = n.nextSibling) nodes.push(n);
			// 				} else nodes.push(knownNode.node);
			// 			}
			// 		}
			// 		return nodes.length > 0 ? {nodes, malus} : null;
			// 	}
			// }
			//Elements fils "normaux"
			if (ctx.preserveUnknown) {
				return content.hasChildNodes() ? {nodes: Array.from(content.childNodes), malus: 0} : null;
			} else {
				const nodes: Node[] = [];
				let malus = 0;
				const findKnown = async (parent: Node, costLost: number) => {
					for (let node = parent.firstChild; node; node = node.nextSibling) {
						const knownNode = await this.mutateNodeToKnownNode(ctx, node, cache);
						if (knownNode) {
							malus += knownNode.malus;
							if (knownNode.node instanceof DocumentFragment) {
								for (let n = knownNode.node.firstChild; n; n = n.nextSibling) nodes.push(n);
							} else nodes.push(knownNode.node);
						} else if (deepFind && node.hasChildNodes()) {
							malus += costLost;
							if (costLost > 50) continue; //rien trouvé sur plus de 3 niveaux, on abandonne ce noeud.
							await findKnown(node, costLost + 20); //Plus on s'enfonce, plus la suppr de noeud est couteux, le 1er niveau est "gratuit" (ex:part collé sur modèle root).
						}
					}
				};
				await findKnown(content, 10);
				//console.log("findKnownNodes::::", nodes);
				if (nodes.length > 0) return {nodes, malus};
			}
		} else {
			const knownNode = await this.mutateNodeToKnownNode(ctx, content, cache);
			if (knownNode) {
				const res = {nodes: [] as Node[], malus: knownNode.malus};
				if (knownNode.node instanceof DocumentFragment) {
					for (let n = knownNode.node.firstChild; n; n = n.nextSibling) res.nodes.push(n);
				} else res.nodes.push(knownNode.node);
				return res.nodes.length > 0 ? res : null;
			}
		}
		return null;
	}

	async tryPasteText(ctx: OSkImportCtxInternal, text: string, cache: OSkImportDatas): Promise<ISkImporter[]> {
		if (!text) return null;
		const node = ctx.schemaDom.document.createTextNode(text);
		const knownNode = await this.mutateNodeToKnownNode(ctx, node, cache);
		//pour un texte, l'insertion peut toujours être insérée n'importe où dans une séquence.
		return knownNode ? [new SkImpSimple(ctx, [knownNode.node], knownNode.malus, EPastePos.anywhere)] : null;
	}

	/** Par défaut, parcourt des noeuds fils ( dans le range donné) pour trouver ceux pouvant accepter un link. */
	async tryPasteLinks<L>(ctx: OSkImportCtxInternal, linksInfo: L, cache: OSkImportDatas): Promise<ISkImporter[]> {
		let offsetMin: number, offsetMax: number;
		let parent = ctx.ctn;
		if (ctx.ctn instanceof Text) {
			//On est dans du mixedContent
			parent = parent.parentNode;
			offsetMin = 0;
			offsetMax = 0;
		} else {
			const startSeq = XA.range2StartSeq(ctx.sel);
			offsetMin = XA.last(startSeq.start) as number;
			offsetMax = offsetMin + startSeq.len;
		}

		const importers: ISkImporter[] = [];
		ctx.skNode.rule.contentRule.findRules((rule: SkRule) => {
			if (rule.skMeta && rule.skMeta._pushLinksImporters !== SkMBase.prototype._pushLinksImporters) {
				const pos = ctx.schemaDom.getPastePos(parent, offsetMin, offsetMax, rule);
				if (pos !== EPastePos.none) rule.skMeta._pushLinksImporters(importers, rule as ISkRuleObj, pos, ctx, linksInfo, cache);
			}
			return false;
		});
		return importers; //Des liens ont été identifiés dans le presse papier, même si pas de result, on retourne [] pour bloquer un import d'une forme dégradée
	}

	async tryImportNodes(ctx: OSkImportCtxInternal, content: Node, cache: OSkImportDatas): Promise<ISkImporter[]> {
		if (DOM.IS_element(content) && content.localName === 'fragment' && content.namespaceURI === DOM.SCCORE_NS) {
			let node = content.firstChild;
			let pos: IImportPos;
			if (ctx.virtualPath) {
				//on est en contexte virtuel
				const lastRule = SKMETA.ctxLastSkRule(ctx);
				if (lastRule instanceof SkRuleNode) {
					const ctRule = lastRule.contentRule;
					const subCard = ctRule.getRealCardSubNode(node.nodeType, node.nodeName);
					if (subCard !== undefined) pos = {insertOffsetMin: 0, insertOffsetMax: 0, similarTo: node} as IImportPos;
				}
			} else {
				pos = ctx.schemaDom.getImportPos(ctx.ctn, node);
			}
			if (pos) {
				const ct = [node];
				for (node = node.nextSibling; node; node = node.nextSibling) ct.push(node);
				return [new SkImpCleanup(ctx, ct, 0, pos)];
			}
		} else if (content instanceof Attr) {
			console.trace("TODO import attribute");
		}
		return null;
	}

	/** Par défaut, parcourt les noeuds fils directs pour trouver ceux pouvant accepter un link géré dans _pushLinksImporters(). */
	async tryImportLinks<L>(ctx: OSkImportCtxInternal, linksInfo: L, cache: OSkImportDatas): Promise<ISkImporter[] | null> | null {
		let importers: ISkImporter[] = null;
		const lastRule = SKMETA.ctxLastSkRule(ctx);
		if (lastRule instanceof SkRuleNode) {
			lastRule.contentRule.findRules((rule: SkRule) => {
				if (rule.skMeta && rule.skMeta._pushLinksImporters !== SkMBase.prototype._pushLinksImporters) {
					let pos: IImportPos;
					if (ctx.virtualPath) {
						//on est contexte virtuel
						if (rule.structType === ENodeType.attribute) {
							pos = {insertAtts: rule.structName, similarTo: rule} as IImportPos;
						} else {
							pos = {insertOffsetMin: 0, insertOffsetMax: 0, similarTo: rule} as IImportPos;
						}
					} else {
						pos = ctx.schemaDom.getImportPos(ctx.ctn, rule);
					}
					if (pos) rule.skMeta._pushLinksImporters(importers || (importers = []), rule as ISkRuleObj, pos, ctx, linksInfo, cache);
				}
				return false;
			});
		}
		return importers;
	}

	_pushLinksImporters<L>(results: ISkImporter[], skRule: ISkRuleObj, pos: EPastePos | IImportPos, ctx: OSkImportCtxInternal, linksInfo: L, cache: OSkImportDatas) {
	}

	/** Transforme un DOM en texte pur (pour insertion dans un texte, un comment, un attribut...). */
	async mutateNodeToText(ctx: OSkImportCtxInternal, content: Node): Promise<string> {
		const atts = SKMETA.getAttsInFragment(content);
		if (atts) {
			//On prend la value du 1er attribut.
			const att = atts.attributes.item(0);
			return att ? att.value : ""
		}
		return content.textContent;
	}

	/**
	 * Transforme un DOM potentiellement inconnu en un DOM au schéma connu.
	 * Ne signifie pas nécessairement que son insertion à un offset donné sera conforme au schema
	 * (pas d'eval de l'ordonnancement ou de la cardinalité), mais le noeud retourné est connu de ce contexte parent.
	 *
	 * @return node si connu, null si ce noeud est définitivement inconnu, un autre noeud si une mutation a été possible.
	 */
	async mutateNodeToKnownNode(ctx: OSkImportCtxInternal, node: Node, cache: OSkImportDatas): Promise<{ node: Node; malus: number }> | null {
		const malus = 0;
		const doc = node.ownerDocument || node;
		if (doc === document || doc instanceof HTMLDocument) {
			//Source HTML => via gram
			node.normalize();
			//console.log("mutateNodeToKnownNode via GRAM SRC::::", DOM.debug(node)); //true
			let gram: IGram = cache.originalHtml === node ? cache.gram : null;
			if (!gram) {
				gram = (await import("lib/edit/import/html2gram.js")).html2gram(node);
				if (cache.originalHtml === node) cache.gram = gram;
				if (!gram) return null;
			}
			//console.log("mutateNodeToKnownNode GRAM::::", gram.asHtml());
			//if (cache.originalDom === node) cache.deepFindDone = true;
			const lastRule = SKMETA.ctxLastSkRule(ctx);
			if (!(lastRule instanceof SkRuleElt)) return null;
			let contentRules = lastRule.contentRule;
			const idx = XA.last(ctx.sel.start);
			if (typeof idx === "number" && idx > 0) {
				//On cherche à insérer après un noeud, on réduit le scope de contentRules possibles.
				const ch = ctx.ctn.childNodes[idx - 1];
				if (ch) {
					const skAfter = ctx.schemaDom.getSkNode(ch);
					if (skAfter) contentRules = contentRules.buildRuleAfter(skAfter.rule);
				}
			}
			let result = (await import("lib/edit/import/gram.js")).transposeGram(gram, contentRules, {
				trspProv: ctx.schemaDom.gramTransposerProv,
				abortIfNotInline: ctx.abortIfNotInline
			});
			//console.log("mutateNodeToKnownNode RES before cleanup::::", result ? result.malus : -1, DOM.debug(result && result.node));
			if (result) {
				if (result.node instanceof DocumentFragment) {
					let ch = result.node.firstChild;
					while (ch) {
						const next = ch.nextSibling;
						result.node.removeChild(ch);
						const subResult = cleanupNodes(ch, ctx, lastRule.contentRule.findRuleNodeFor(ch), 0, lastRule);
						result.malus += subResult.malus;
						result.node.insertBefore(subResult.node, next);
						ch = next;
					}
				} else {
					result = cleanupNodes(result.node, ctx, lastRule.contentRule.findRuleNodeFor(result.node), result.malus, lastRule);
				}
			}
			//console.log("mutateNodeToKnownNode RESULT::::", result ? result.malus : -1, DOM.debug(result && result.node));
			return result;
		} else {
			//Source XML => via similitude du dom source et cible
			let parentRule: SkRuleNode;
			if (ctx.virtualPath) {
				const lastRule = SKMETA.ctxLastSkRule(ctx);
				if (!(lastRule instanceof SkRuleNode)) throw Error(this.id + " must not be an attribute.");
				parentRule = lastRule;
			} else {
				parentRule = ctx.skNode.rule;
			}
			if (IS_comment(node)) return parentRule.getUnknownNodeRule(ctx.skNode, node) ? {node, malus: 0} : null;
			const subRule = parentRule.contentRule.findRuleNodeFor(node);
			if (subRule) {
				//ce noeud est connu du schéma.
				//Corrections / mutations internes.
				//On décroche node de son contexte parent, pour que les XAddr soient identiques si des corrections sur un clone doivent être appliquées.
				const oldNode = node;
				const nextS = node.nextSibling;
				const parent = node.parentNode;
				if (parent) parent.removeChild(node);
				const result = cleanupNodes(node, ctx, subRule, malus, parentRule);
				//On replace le node non modifié dans son contexte initial.
				if (parent) parent.insertBefore(oldNode, nextS);
				return result
			}
			//noeud inconnu => on recherche une conversion.
			const rules = parentRule.contentRule.findRules((r) => r instanceof SkRuleElt) as SkRuleElt[];
			if (rules) {
				//On retourne la 1ère conversion valide en parcourant les SkRuleElt fils de parentRule.
				const src = {
					node: node
				} as IConvertSrc;
				for (const r of rules) {
					if (r.convertFrom) {
						src.outStruct = r;
						const cnv = r.convertFrom.findConverter(src);
						if (cnv) {
							const dst = await cnv.convert(src);
							if (dst) {
								const nodeMut = CONVERT.getDstAsNode(dst);
								if (nodeMut) return {node: nodeMut, malus};
							}
						}
					}
				}
			}
		}
		return null;
	}

	normChars(ctn: Text | Attr, msgs: MsgOt[]) {}

	onExecRulesStart(skCtx: SkContext) {}

	onExecRules(skCtx: SkContext, rule: ISkRuleObj, nodeOrAttr: Node | Attr): void {}

	overwriteAlternates(skAnc: SkNode, parent: SkRuleNode, toReplace: ISkRuleObj, offset: number, nodeType: ENodeType, nodeName: string, result: ISkStructDef[]): ISkStructDef[] {return result}

	isInsertable(parent: SkNode, rule: ISkRuleObj): boolean {return true}

	isTextSearchable(skNode: SkNode, nodeOrAttr: Node | Attr): boolean {return false}

	computeGramBindingMalus(gram: IGram, rule: ISkRuleObj, childrenMalus: number): number {
		const coef = rule.skMalusCoef;
		return coef != null ? (gram.weight * coef) + childrenMalus : childrenMalus;
	}

	getAspect<A>(keyAspect: string, rule: SkRuleNode | SkRuleAttr, skNode: SkNode, options: any): A {return null}
}

export class SkMGenericElt extends SkMBase {
	get gramLevelType(): EGramLevelType {return EGramLevelType.block}

	//NON géré via ctx.preserveUnknown. /** Xml généric, on accepte tout. */
	// async mutateNodeToKnownNode(ctx: OSkPasteCtxInternal, node: Node): Promise<{ node: Node; malus: number }> {
	// 	return {node, malus: 0};
	// }

	isTextSearchable(skNode: SkNode, nodeOrAttr: Node | Attr): boolean {return true}
}

export class SkMGenericAtt extends SkMBase {

	async tryPasteText(ctx: OSkImportCtxInternal, text: string, cache: OSkImportDatas): Promise<ISkImporter[]> {
		//TODO si text multilines : retourne 2 ISkImporter (1ère ligne / toutes les lignes séparées par un espace)
		return text ? [new SkImpSimple(ctx, text.replace(/\n\r|\r\n|\r|\n/g, ' '))] : null;
	}

	async tryPasteNodes(ctx: OSkImportCtxInternal, content: Node, cache: OSkImportDatas): Promise<ISkImporter[]> {
		const text = await this.mutateNodeToText(ctx, content);
		return text ? this.tryPasteText(ctx, text, cache) : null;
	}

	isTextSearchable(skNode: SkNode, nodeOrAttr: Node | Attr): boolean {return true}

	onExecRules(skCtx: SkContext, rule: SkRuleAttr, nodeOrAttr: Attr): void {
		if (skCtx.execOptions.autoNormXml) {
			//On suppr les déclarations de namespaces
			if (nodeOrAttr.nodeName === 'xmlns' || nodeOrAttr.prefix === 'xmlns') {
				skCtx.execOptions.corrections.push(new XmlStrMsg().init(XA.from(nodeOrAttr), null));
			}
		}
	}
}

/**
 * Attr avec valeur forcée
 */
export class SkMFixedAttr extends SkMGenericAtt {
	initSkRule(skRule: IFixedAttrRule, confRule: Element): void {
		if (confRule.hasAttributeNS(SK_NS, "fixedValue")) skRule.fixedValue = confRule.getAttributeNS(SK_NS, "fixedValue") || "";
	}

	//todo rendre configuarble dans initSkRule ?
	isTextSearchable(skNode: SkNode, nodeOrAttr: Node | Attr): boolean {return false}

	onExecRules(skCtx: SkContext, rule: IFixedAttrRule, nodeOrAttr: Attr): void {
		if (skCtx.execOptions.autoComplete && "fixedValue" in rule) {
			if (!nodeOrAttr || nodeOrAttr.value == null || nodeOrAttr.value != rule.fixedValue)
				skCtx.execOptions.corrections.push(new XmlStrMsg().init(XA.from(nodeOrAttr), rule.fixedValue));
		}
	}
}

export interface IFixedAttrRule extends SkRuleAttr {
	skMeta: SkMFixedAttr
	fixedValue: string
}

SKMETALIB.registerMetaNode(new SkMFixedAttr('FixedAttr'));


export class SkMComment extends SkMBase {

	async tryPasteNodes(ctx: OSkImportCtxInternal, content: Node, cache: OSkImportDatas): Promise<ISkImporter[]> {
		const text = await this.mutateNodeToText(ctx, content);
		return text ? [new SkImpSimple(ctx, text)] : null;
	}

	async tryPasteText(ctx: OSkImportCtxInternal, text: string, cache: OSkImportDatas): Promise<ISkImporter[]> {
		if (!text) return null;
		return [new SkImpSimple(ctx, text, 0, EPastePos.anywhere)];
	}
}

export class SkMDocument extends SkMBase {
}

SKMETALIB.registerMetaNode(new SkMGenericElt('*'));
SKMETALIB.registerMetaNode(new SkMGenericAtt('@'));
SKMETALIB.registerMetaNode(new SkMComment('!'));
SKMETALIB.registerMetaNode(new SkMDocument('/'));

/** Elimine ces éléments "silencieusement". */
class SkMBlackHole extends SkMBase {
	get gramLevelType(): EGramLevelType {return EGramLevelType.block}

	initSkRule(skRule: SkRuleNode, confRule: Element): void {
		skRule.card = '*'; //On force une card * : du point de vue RNG cet élement ne devrait pas exister.
		if (skRule instanceof SkRuleElt) skRule.noAutoComplete = true; //Pas de création auto de ce contenu.
		skRule.initContentRule(SkRuleChoice.ANY); //on force le contentRule à ANY et non EMPTY.
	}

	onExecRules(skCtx: SkContext, rule: ISkRuleObj, nodeOrAttr: Node | Attr): void {
		if (skCtx.execOptions.autoComplete) {
			skCtx.execOptions.corrections.push(new XmlDeleteMsg().init(XA.from(nodeOrAttr), 1));
		} else if (skCtx.execOptions.genAnnots) {
			skCtx.addAnnot(new SkAnnotEltFree().init(nodeOrAttr as Element, "Cet élément devrait être éliminé dans ce contexte."));
		}
	}

	isInsertable(parent: SkNode, rule: ISkRuleObj): boolean {return false}
}

SKMETALIB.registerMetaNode(new SkMBlackHole('BlackHole'));

/** Affiche ces éléments si ils existent mais ne les propose pas dans les listes d'insertion. */
class SkMDeprecated extends SkMBase {
	get gramLevelType(): EGramLevelType {return EGramLevelType.block}

	initSkRule(skRule: SkRule & ISkMDeprecatedProps, confRule: Element): void {
		super.initSkRule(skRule, confRule);
		skRule.deprecatedMsg = confRule.getAttributeNS(SK_NS, 'deprecatedMsg');
	}

	onExecRules(skCtx: SkContext, rule: ISkRuleObj & ISkMDeprecatedProps, node: Element): void {
		super.onExecRules(skCtx, rule, node);
		skCtx.addAnnot(new SkAnnotEltFree().init(node, rule.deprecatedMsg || "Élément déprécié", EAnnotLevel.warning));
	}

	isInsertable(parent: SkNode, rule: ISkRuleObj): boolean {return false}
}

type ISkMDeprecatedProps = {
	deprecatedMsg?: string
}

SKMETALIB.registerMetaNode(new SkMDeprecated('Deprecated'));


//******************* Modeling *******************

/** Balise racine d'un item (sc:item). Définit ici pour debug. Surchargé dans Wsp. */
SKMETALIB.registerMetaNode(new SkMGenericElt('Item'));

/** SkMObject: container possédant optionnellement un 1er fils SkMMeta à exporter avec le container. */
export abstract class SkMObject extends SkMBase {
	forceExportContainer: boolean;

	// hasMeta(src: SkNode) {
	// 	let ch = src.node.firstElementChild;
	// 	if (ch) {
	// 		let skCh = src.schemaDom.getSkNode(ch);
	// 		if (skCh && (skCh.rule.skMeta instanceof SkMMeta)) return true;
	// 	}
	// 	return false;
	// }

	getOffsetAfterMeta(src: SkNode) {
		const ch = src.node.firstElementChild;
		if (ch) {
			const skCh = src.schemaDom.getSkNode(ch);
			if (skCh && (skCh.rule.skMeta instanceof SkMMeta)) return DOM.computeOffset(ch);
		}
		return 0;
	}

	getMetaRule(rule: SkRule): SkRuleElt | null {
		return (rule as SkRuleElt).contentRule?.findRule((r) => r.skMeta instanceof SkMMeta) as SkRuleElt;
	}

	/** On exporte toujours les metas de l'objet avec l'objet. */
	exportContainerNode(src: SkNode, parentTarget: Node | IJmlSet | null, scope?: OSkMCopyScope, fromDepth?: number | null, toDepth?: number | null): Node | IJmlSet {
		if (fromDepth != null && toDepth != null && !this.forceExportContainer) return null;//copy d'un fragment du contenu => pas de container
		const clone = parentTarget ? JML.appendDomNode(src.node, false, parentTarget) : []; //on force un container pour calcucler les noeuds copiés.
		const ch = src.node.firstElementChild;
		if (ch) {
			const skCh = src.schemaDom.getSkNode(ch);
			if (skCh && (skCh.rule.skMeta instanceof SkMMeta)) {
				//le 1er fils est bien un meta de l'objet.
				skCh.rule.skMeta.exportNode(skCh, clone, scope);
			}
		}
		return clone;
	}

	exportContent(src: SkNode, parentTarget: Node | IJmlSet) {
		let ch = src.node.firstChild;
		if (ch) {
			const skCh = src.schemaDom.getSkNode(ch);
			if (skCh && (skCh.rule.skMeta instanceof SkMMeta)) ch = ch.nextSibling;
			while (ch) {
				JML.appendDomNode(ch, true, parentTarget);
				ch = ch.nextSibling;
			}
		}
	}
}

export class SkMCompo extends SkMObject {

	get gramLevelType(): EGramLevelType {return EGramLevelType.block}
}

SKMETALIB.registerMetaNode(new SkMCompo('Compo'));

export class SkMPart extends SkMObject {

	get gramLevelType(): EGramLevelType {return EGramLevelType.block}

	// tryPasteLinks<L>(ctx: OSkPasteImportCtxInternal, linksInfo: L, cache: OSkImportDatas): Promise<ISkImporter[]> {
	// 	console.log("tryPasteLinks in part:::::::", linksInfo);
	// 	return Promise.resolve(null);
	// }

	exportAsHtml(node: Node, rule: SkRule, outParent: HTMLElement): void {
		if (rule.skFamily === "sub-level") {
			const sec = outParent.appendChild(outParent.ownerDocument.createElement("section"));
			super.exportAsHtml(node, rule, sec);
		} else {
			super.exportAsHtml(node, rule, outParent);
		}
	}
}

SKMETALIB.registerMetaNode(new SkMPart('Part'));

export class SkMMeta extends SkMBase {

	get gramLevelType(): EGramLevelType {return EGramLevelType.metas}
}

SKMETALIB.registerMetaNode(new SkMMeta('Meta'));

export class SkMField extends SkMBase {

	get gramLevelType(): EGramLevelType {return EGramLevelType.metas}

	exportAsText(node: Node, rule: SkRule, buf: string[]) {
		if (node.hasChildNodes()) {
			if (rule.skFamily === "property/title") {
				super.exportAsText(node, rule, buf);
			} else if (rule.skFamily === "property/url") {
				// ?
			} else {
				//Publication des props ajouter un flag ? skFamily='labeled' ?
				// buf.push(rule.structLabel, ": ");
				// super.exportAsText(node, rule, buf);
				// if (DOM.findFirstChild(node, DOM.IS_text)) {
				// 	//Si c'est un field de text renseigné, on ajoute un saut de ligne.
				// 	buf.push("\n");
				// }
			}
		}
	}

	exportAsHtml(node: Node, rule: SkRule, outParent: HTMLElement): void {
		if (node.hasChildNodes()) {
			if (rule.skFamily === "property/title") {
				let ti: HTMLElement;
				if (DOM.findFirstChild(outParent, (n: Node): n is Node => n.nodeName.length === 2 && n.nodeName[0] === 'h')) {
					//Un titre a déjà été publié dans ce contexte.
					//On ajoute un p + b en guise de faux titre.
					const p = outParent.appendChild(outParent.ownerDocument.createElement("p"));
					ti = p.appendChild(outParent.ownerDocument.createElement("b"));
				} else {
					//1er titre dans ce contexte.
					let depth = 0;
					DOM.findParentOrSelf(outParent, null, (n: Node): n is Node => {
						if (n.nodeName === 'section') depth++;
						return depth == 6;
					});
					ti = outParent.appendChild(outParent.ownerDocument.createElement("h" + Math.max(1, depth)));
				}
				super.exportAsHtml(node, rule, ti);
				const ch = ti.firstElementChild;
				if (ch && ch.localName === 'p') {
					//On cleanup le p dans la balise hx
					while (ch.firstChild) ti.appendChild(ch.firstChild);
					ch.remove();
				}
			} else if (rule.skFamily === "property/url") {
				if (outParent instanceof HTMLAnchorElement) {
					outParent.href = node.textContent;
				}
			} else if (rule.skFamily === "content") {
				super.exportAsHtml(node, rule, outParent);
			} else {
				//Publication des props ajouter un flag ? skFamily='labeled' ?
				// const doc = outParent.ownerDocument;
				// const div = outParent.appendChild(doc.createElement("div"));
				// const label = div.appendChild(doc.createElement("label"));
				// label.appendChild(doc.createTextNode(rule.structLabel + ": "));
				// super.exportAsHtml(node, rule, div);
			}
		}
	}

	//TODO ajouter conf du field ? ws='asis/replace/collapse/trim'. impl actuelle : trim (avec collapse).
	normChars(ctn: Text | Attr, msgs: MsgOt[], noTrim?: boolean, val?: string) {
		//trim
		const str = val || ctn.nodeValue;
		const spEnd = noTrim ? -1 : DOM.txtEndSpLen(str);
		if (spEnd > 0) {
			msgs.push(new XmlDeleteMsg().init(XA.append(XA.from(ctn), str.length - spEnd), spEnd));
		}
		const spStart = noTrim ? -1 : DOM.txtStartSpLen(str);
		if (spStart > 0) {
			msgs.push(new XmlDeleteMsg().init(XA.append(XA.from(ctn), 0), spStart));
		}
		//Collapse
		const re = DOM.COLLAPSE_WS;
		re.lastIndex = 0;
		let res: RegExpExecArray;
		while ((res = re.exec(str))) {
			if (spStart > 0 && res.index === 0) continue; //déjà traité
			if (spEnd > 0 && res.index === str.length - res[0].length) continue; //déjà traité
			const xa = XA.append(XA.from(ctn), res.index);
			msgs.push(new XmlDeleteMsg().init(xa, res[0].length), new XmlInsertMsg().init(xa, ' '));
		}
	}

	//TODO éliminer les fields avec un format de stockage non signifiant pour le user (enums, date, id...)
	// => ajouter @skm:search="text/date/id/..." ?
	isTextSearchable(skNode: SkNode, nodeOrAttr: Node | Attr): boolean {
		if (skNode.rule.contentRule instanceof SkRuleChoice) return false; //enum
		if (skNode.rule.contentRule instanceof SkRuleChoice) return false; //enum
		return true
	}
}

SKMETALIB.registerMetaNode(new SkMField('Field'));


/**
 * Attribut de type Id avec validation synchrone et locale au document.
 */
class SkMId extends SkMBase {

	initSkRule(skRule: IIdAttrRule, confRule: Element): void {
		skRule.crossMgrId = confRule.getAttributeNS(SK_NS, "crossMgrId") || "";
	}

	onExecRules(skCtx: SkContext, rule: IIdAttrRule, attr: Attr): void {
		const opts = skCtx.execOptions;
		let id: string;
		if (opts.autoComplete) {
			if (!attr.nodeValue) {
				const mgr = skCtx.skNode.schemaDom.crossDomMgrs[rule.crossMgrId] as IXmlIdMgr;
				id = this.genId(rule, opts, id, mgr);
				opts.corrections.push(new XmlStrMsg().init(XA.from(attr), id));
			}
		}
		if (opts.genAnnots) {
			if (!id) id = attr.nodeValue;
			if (id) {
				const mgr = skCtx.skNode.schemaDom.crossDomMgrs[rule.crossMgrId] as IXmlIdMgr;
				if (mgr.updateMap(id, attr, skCtx, opts.autoMutate) === "conflict") {
					if (opts.autoMutate) {
						id = this.genId(rule, opts, id, mgr);
						(opts.mutations || (opts.mutations = [])).push(new XmlStrMsg().init(XA.from(attr), id));
					} else {
						skCtx.addAnnot(new SkAnnotWrongValue().init(attr, "Cet identifiant n'est pas unique dans le document"));
					}
				}
			}
		}
	}

	private genId(rule: IIdAttrRule, opts: OSkExecInternalOptions, id: string, mgr: IXmlIdMgr) {
		const key = "__ids:" + rule.crossMgrId;
		const setIds = (opts as any)[key] || ((opts as any)[key] = new Set()) as Set<string>;
		id = mgr.generateId(setIds);
		setIds.add(id);
		return id;
	}

	getAspect<A>(keyAspect: string, rule: SkRuleNode | SkRuleAttr, skNode: SkNode, options: any): A {
		if (keyAspect === "XmlIdMgr") return skNode.schemaDom.crossDomMgrs[(rule as IIdAttrRule).crossMgrId] as A;
		return null;
	}
}

export interface IIdAttrRule extends SkRuleAttr {
	skMeta: SkMId
	crossMgrId: string
}


SKMETALIB.registerMetaNode(new SkMId('IdAttr'));


class SkMAutoFillAsync extends SkMField {

	initSkRule(skRule: IAutofillAsyncRule, confRule: Element): void {
		skRule.crossMgrId = confRule.getAttributeNS(SK_NS, "crossMgr") || "";
	}

	async onExecRules(skCtx: SkContext, rule: IAutofillAsyncRule, nodeOrAttr: Node | Attr) {
		const opts = skCtx.execOptions;
		if (opts.autoComplete) {
			const schemaDom = skCtx.skNode.schemaDom;
			const mgr = schemaDom.crossDomMgrs[rule.crossMgrId] as IAutoFillAsyncMgr;
			if (mgr.needNewVal(schemaDom, nodeOrAttr, rule)) {
				const value = await mgr.newValue(schemaDom, nodeOrAttr, rule);
				if (value != null && (nodeOrAttr instanceof Attr ? nodeOrAttr.ownerElement?.isConnected : nodeOrAttr.isConnected && mgr.needNewVal(schemaDom, nodeOrAttr, rule))) {
					schemaDom.house.executeBatch(new XmlBatch().setMeta('noFocus', true).add(nodeOrAttr instanceof Element ?
						replaceXmlContent(nodeOrAttr, [value])
						: new XmlStrMsg().init(XA.from(nodeOrAttr), value)
					));
				}
			}
		}
	}

	getAspect<A>(keyAspect: string, rule: SkRuleNode | SkRuleAttr, skNode: SkNode, options: any): A {
		if (keyAspect === "AutoFillAsyncMgr") return skNode.schemaDom.crossDomMgrs[(rule as IAutofillAsyncRule).crossMgrId] as A;
		return null;
	}
}

export interface IAutoFillAsyncMgr extends ISkCrossDomMgr {

	needNewVal(schemaDom: SchemaDom, nodeOrAttr: Node | Attr, rule: IAutofillAsyncRule): boolean

	/** Si retourne null, aucune modif n'est effectuée. */
	newValue(schemaDom: SchemaDom, nodeOrAttr: Node | Attr, rule: IAutofillAsyncRule): Promise<string | null>
}

export interface IAutofillAsyncRule extends SkRuleAttr {
	skMeta: SkMAutoFillAsync
	crossMgrId: string
}

SKMETALIB.registerMetaNode(new SkMAutoFillAsync('AutoFillAsync'));


export abstract class SkImpBase implements ISkImporter {

	malus: number;

	pastePos?: EPastePos;
	importPos?: IImportPos;

	protected schemaDom: SchemaDom;

	protected constructor(schemaDom?: SchemaDom, pos?: EPastePos | IImportPos) {
		if (schemaDom) this.schemaDom = schemaDom;
		if (pos != null) {
			if (typeof pos === 'number') this.pastePos = pos;
			else this.importPos = pos;
		}
	}

	getLabel(): string {return this.malus > 0 ? "Collage partiel" : "Collage complet"}

	doImport(context: OSkPasteContext, batch: XmlBatch): void {
		throw Error("Not implemented");
	}

}

/** Import générique d'un fragment Jml. */
export class SkImpSimple extends SkImpBase {

	contentToImport: Node[] | string;

	constructor(ctx: OSkImportCtxInternal, content: Node[] | string, malus = 0, pos?: EPastePos | IImportPos) {
		super(ctx.schemaDom, pos);
		this.contentToImport = content;
		this.malus = malus;
	}

	doImport(context: OSkPasteContext, batch: XmlBatch): void {
		const ctx = SKMETA.buildSkImportCtxInternal(context, this.schemaDom);
		const content = this.getJml(ctx);
		if (content) SKMETA.doPasteReplaceSel(content, ctx, this.schemaDom, batch);
	}

	protected getJml(ctx: OSkImportCtxInternal): IJmlSet | string {
		if (typeof this.contentToImport === 'string') return this.contentToImport;
		const jml: IJmlSet = [];
		if (ctx.ctn instanceof Element) {
			if (ctx.sel) {
				//Issu d'un OSkPasteContext, on filtre this.contentToImport
				let offsetMin: number, offsetMax: number;
				SKMETA.ctxLastSkRule(ctx);//Force le calcul de ctx.skRuleStack
				if (ctx.skRuleStack) {
					//On insère en contexte virtuel
					offsetMin = offsetMax = ctx.sel.start[ctx.sel.start.length - 1 - ctx.virtualPath.length] as number;
					const pos = ctx.schemaDom.getPastePos(ctx.ctn, offsetMin, offsetMax, ctx.skRuleStack[0]);
					if (pos & EPastePos.replace || pos & EPastePos.before) {
						for (const node of this.contentToImport) {
							JML.domNode2jml(this.cleanupNode(ctx, node), jml, {mergeTextNodes: true});
						}
					}
				} else {
					if (ctx.virtualPath) {
						//Cas d'un seul noeud text virtuel : ctx.virtualPath existe (=['#']) mais pas ctx.skRuleStack
						offsetMin = ctx.sel.start[ctx.sel.start.length - 2] as number;
						offsetMax = offsetMin;
					} else {
						const startSeq = XA.range2StartSeq(ctx.sel);
						offsetMin = XA.last(startSeq.start) as number;
						offsetMax = offsetMin + startSeq.len;
					}
					for (const node of this.contentToImport) {
						const pos = IS_comment(node) ? EPastePos.anywhere : ctx.schemaDom.getPastePos(ctx.ctn, offsetMin, offsetMax, node);
						if (pos & EPastePos.replace || pos & EPastePos.before) {
							JML.domNode2jml(this.cleanupNode(ctx, node), jml, {mergeTextNodes: true});
						}
					}
				}
				return jml.length > 0 ? jml : null;
			}
		}
		for (const node of this.contentToImport) JML.domNode2jml(node, jml, {mergeTextNodes: true});
		return jml;
	}

	protected cleanupNode(ctx: OSkImportCtxInternal, node: Node): Node {
		return node;
	}
}

/**
 * Execute l'import avec cleanup des nodes à importer. A utiliser lorsque le cleanup n'a pas encore
 * été réalisé, ie via SchemaDom.tryImport() pour des raisons de perf, contrairement à SchemaDom.tryPasteXxx où le cleanup a déjà été réalisé.
 */
export class SkImpCleanup extends SkImpSimple {

	protected cleanupNode(ctx: OSkImportCtxInternal, node: Node): Node {
		if (node.parentNode) node.parentNode.removeChild(node);
		const rule = ctx.skRuleStack ? ctx.skRuleStack[ctx.skRuleStack.length - 1] as SkRuleNode : ctx.skNode.rule;
		return cleanupNodes(node, ctx, rule.contentRule.findRuleNodeFor(node), 0, rule).node;
	}
}


/** Wrapper d'un ISkImporter pour réaliser un pre / post traitement au doImport(). */
abstract class SkImpWrapper extends SkImpBase implements ISkImporter {

	// @ts-ignore
	get pastePos() {return this.subImporter.pastePos}

	// @ts-ignore
	get importPos() {return this.subImporter.importPos}

	protected constructor(schemaDom: SchemaDom, protected subImporter: ISkImporter, malus: number = 0) {
		super(schemaDom);
		this.malus = malus;
	}

	getLabel(): string {return this.subImporter.getLabel()}

	get needAsyncBuild() {return this.subImporter.needAsyncBuild};

	buildContentToImport(uiContext?: HTMLElement): Promise<void | "stop"> {return this.subImporter.buildContentToImport(uiContext)}


	doImport(context: OSkPasteContext, batch: XmlBatch): void {
		return this.subImporter.doImport(context, batch);
	}
}


/**
 * Wrap un ISkImporter pour executer un split préalable.
 * Exemple dans Txt : la sel / caret est dans un para, mais on copie des blocks, on doit splitter jusqu'au para, puis insérer.
 */
export class SkImpSplitUp extends SkImpWrapper {
	constructor(schemaDom: SchemaDom, subImporter: ISkImporter, protected splitDepth: number, malus = 0) {
		super(schemaDom, subImporter, malus);
	}

	doImport(context: OSkPasteContext, batch: XmlBatch): void {
		context = Object.create(context); //surcharges sans altérer le context orginal.
		let splitDepth = this.splitDepth;
		if (context.virtualPath) {
			//suppr les noeuds virtuels du split.
			const rem = Math.min(splitDepth, context.virtualPath.length);
			context.sel = {start: context.sel.start.slice(0, context.sel.start.length - rem)};
			context.virtualPath = context.virtualPath.length === rem ? null : context.virtualPath.slice(0, context.virtualPath.length - rem);
			splitDepth -= rem;
		}
		if (splitDepth > 0) {
			const splitPoint = XA.incrAtDepth(context.sel.start.slice(0, context.sel.start.length - splitDepth), -1, 1);
			//on élimine le contenu à splitter et on suppr la sel
			if (XA.isCollapsed(context.sel)) {
				const toMove = [] as IJmlSet;
				this.schemaDom.exportRange({start: context.sel.start, end: splitPoint, deletes: batch}, toMove);
				//on insère le contenu splitté
				batch.add(new XmlInsertMsg().init(splitPoint, toMove));
			} else if (XA.isBefore(context.sel.end, splitPoint)) {
				//toute la sel est avant le point d'insertion du split.
				const toMove = [] as IJmlSet;
				this.schemaDom.exportRange({start: context.sel.end, end: splitPoint}, toMove);
				this.schemaDom.exportRange({start: context.sel.start, end: splitPoint, deletes: batch}, null);
				//on insère le contenu splitté
				batch.add(new XmlInsertMsg().init(splitPoint, toMove));
			} else {
				//la sel inclus le point d'insertion, on suppr simplement la sel
				this.schemaDom.exportRange({start: context.sel.start, end: context.sel.end, deletes: batch}, null);
			}
			//on replace le point d'insertion
			context.sel = {start: splitPoint};
		}
		this.subImporter.doImport(context, batch);
	}
}

/**
 * Wrap un ISkImporter pour exécuter une remonté de l'insertion préalable.
 */
export class SkImpGoUp extends SkImpWrapper {
	constructor(schemaDom: SchemaDom, subImporter: ISkImporter, protected upDepth: number, protected insPos: 'before' | 'after' | 'replace', malus = 0) {
		super(schemaDom, subImporter, malus);
	}

	doImport(context: OSkPasteContext, batch: XmlBatch): void {
		const ctx = Object.create(context) as OSkImportCtxInternal; //surcharges sans altérer le context orginal.
		let upDepth = this.upDepth;
		if (ctx.virtualPath) {
			//suppr les noeuds virtuels du split.
			const rem = Math.min(upDepth, ctx.virtualPath.length);
			ctx.sel = {start: ctx.sel.start.slice(0, ctx.sel.start.length - rem)};
			ctx.virtualPath = ctx.virtualPath.length === rem ? null : ctx.virtualPath.slice(0, ctx.virtualPath.length - rem);
			upDepth -= rem;
			ctx.skRuleStack = undefined;
		}
		if (upDepth > 0) {
			let insPoint = ctx.sel.start.slice(0, ctx.sel.start.length - upDepth);
			switch (this.insPos) {
			case 'replace':
				if (!XA.isCollapsed(context.sel)) this.schemaDom.exportRange({start: insPoint, end: context.sel.end, deletes: batch}, null);
				break;
			case 'after':
				if (!XA.isCollapsed(context.sel)) this.schemaDom.exportRange({start: context.sel.start, end: context.sel.end, deletes: batch}, null);
				insPoint = XA.incrAtDepth(insPoint, -1, 1);
				break;
			}
			//on replace le point d'insertion
			ctx.sel = {start: insPoint};
		}
		this.subImporter.doImport(ctx, batch);
	}

}


export namespace SKMETA {

	/** Crée un nouveau contexte d'import sur un SkRule ancêtre de ctx. */
	export function newImportCtxUp(ctx: OSkImportCtxInternal, filter?: (skRule: SkRule) => boolean): OSkImportCtxInternal {
		const newCtx = Object.create(ctx) as OSkImportCtxInternal;

		function goUp(deltaUp: number) {
			if (ctx.virtualPath && ctx.virtualPath[ctx.virtualPath.length - 1] === '#') deltaUp++; //deltaUp est calculé sur skRuleStack qui élimine le # final.
			if (ctx.sel) {
				newCtx.sel = {start: ctx.sel.start.slice(0, ctx.sel.start.length - deltaUp)};
			} else {
				newCtx.xaParent = ctx.xaParent.slice(0, ctx.xaParent.length - deltaUp);
			}
			if (ctx.virtualPath) newCtx.virtualPath = ctx.virtualPath.length > deltaUp ? ctx.virtualPath.slice(0, ctx.virtualPath.length - deltaUp) : null;
		}

		if (ctx.skRuleStack) {
			for (let i = ctx.skRuleStack.length - 2; i >= 0; i--) {
				const skRule = ctx.skRuleStack[i];
				if (!filter || filter(skRule)) {
					newCtx.skRuleStack = ctx.skRuleStack.slice(0, i + 1);
					goUp(ctx.skRuleStack.length - i - 1);
					return newCtx;
				}
			}
			//
			newCtx.skRuleStack = null; //on remonte au dessus de skRuleStack.
			if (!filter || filter(ctx.skNode.rule)) {
				goUp(ctx.skRuleStack.length);
				return newCtx;
			}
		}
		let parent = ctx.skNode.node.parentNode;
		const originalDepth = newCtx.insertDepth;
		while (parent) {
			newCtx.skNode = ctx.schemaDom.getSkNode(parent);
			newCtx.insertDepth--;
			if (!filter || filter(newCtx.skNode.rule)) {
				newCtx.ctn = newCtx.skNode.node;
				goUp(originalDepth - newCtx.insertDepth + (ctx.skRuleStack ? ctx.skRuleStack.length : 0));
				return newCtx;
			}
			parent = parent.parentNode;
		}
		return null;
	}

	export function buildSkImportCtxInternal(this: void, context: OSkPasteContext | OSkPasteSearchContext, schemaDom: SchemaDom): OSkImportCtxInternal {
		const ctx = context as OSkImportCtxInternal;
		ctx.schemaDom = schemaDom;
		const sel = (context as OSkPasteContext).sel;
		if (sel) {
			//OSkPasteContext
			const xaStart = sel.start;
			if (XA.isCollapsed(sel)) {
				ctx.insertDepth = xaStart.length - (ctx.virtualPath ? ctx.virtualPath.length + 1 : 1);
			} else {
				ctx.insertDepth = xaStart.length - 1; //XA.findForkDepth(xaStart, sel.end);
			}
			ctx.ctn = XA.findDom(xaStart, schemaDom.nodeRoot, ctx.insertDepth);
		} else {
			//OSkPasteSearchContext
			const xaParent = (context as OSkPasteSearchContext).xaParent;
			ctx.insertDepth = ctx.virtualPath ? xaParent.length - ctx.virtualPath.length : xaParent.length;
			ctx.ctn = XA.findDom(xaParent, schemaDom.nodeRoot, ctx.insertDepth);
		}
		let skNode = ctx.schemaDom.getSkNode(ctx.ctn);
		if (!skNode) {
			if (ctx.ctn instanceof Text) {
				skNode = ctx.schemaDom.getSkNode(ctx.ctn.parentNode);
			} else if (ctx.ctn instanceof Attr) {
				skNode = ctx.schemaDom.getSkNode(ctx.ctn.ownerElement);
				const attRule = skNode.rule.contentRule.findRuleAttrFor(ctx.ctn.nodeName) || skNode.rule.getUnknownAttRule(skNode, ctx.ctn.nodeName);
				ctx.skRuleStack = [attRule];
			} else if (ctx.ctn instanceof Comment) {
				skNode = ctx.schemaDom.getSkNode(ctx.ctn.parentNode);
				ctx.skRuleStack = [skNode.rule.getUnknownNodeRule(skNode, ctx.ctn)];
			}
			if (!skNode) throw Error("SkNode not found for " + DOM.debug(ctx.ctn));
		}
		ctx.skNode = skNode;
		return ctx;
	}

	export function isAtBorder(node: Node, start: boolean, offset: number, schemaDom: SchemaDom): boolean {
		if (node.nodeType === ENodeType.element) {
			const skNode = schemaDom.getSkNode(node);
			if (skNode && 'isAtBorder' in skNode.rule.skMeta) return (skNode.rule.skMeta as any).isAtBorder(skNode, start, offset, schemaDom);
			if (start) {
				if (offset > 0) for (let n = node.childNodes.item(offset - 1) as Node; n; n = n.previousSibling) if (!isEmpty(n, schemaDom)) return false;
				return true;
			} else {
				for (let n = node.childNodes.item(offset) as Node; n; n = n.nextSibling) if (!isEmpty(n, schemaDom)) return false;
				return true;
			}
		} else if (node.nodeType === ENodeType.text) {
			return start ? offset === 0 : offset === node.nodeValue.length;
		}
		return false;
	}

	/** Ce noeud est-il logiquement vide ? */
	export function isEmpty(node: Node, schemaDom: SchemaDom): boolean {
		if (node.nodeType === ENodeType.text) return node.nodeValue.length === 0;
		//A voir si utile...
		// let skNode = schemaDom.getSkNode(node);
		// if (skNode && 'isEmpty' in skNode.rule.skMeta) return (skNode.rule.skMeta as any).isEmpty(node, schemaDom);
		return false;
	}

	/** Offset de l'insertion dans ctx.ctn. */
	export function ctxInsertStartOffset(ctx: OSkImportCtxInternal): number | string {
		return ctx.sel.start[ctx.insertDepth];
	}

	/** Offset dans ctx.ctn de la séquence supprimée (= ctxInsertStartOffset() si sel collapsed). */
	export function ctxInsertEndOffset(ctx: OSkImportCtxInternal): number | string {
		return ctx.sel.end ? ctx.sel.end[ctx.insertDepth] : ctx.sel.start[ctx.insertDepth];
	}

	/** Profondeur du point d'insertion dans le dernier noeud réel. */
	export function ctxRealStartDepth(ctx: OSkImportCtxInternal): number {
		if (ctx.xaParent) return ctx.virtualPath ? ctx.xaParent.length - ctx.virtualPath.length : ctx.xaParent.length;
		return ctx.virtualPath ? ctx.sel.start.length - ctx.virtualPath.length - 1 : ctx.sel.start.length - 1;
	}

	/** Profondeur du container d'insertion (réel ou virtuel). */
	export function ctxContainerDepth(ctx: OSkImportCtxInternal): number {
		if (ctx.xaParent) return ctx.xaParent.length;
		return ctx.sel.start.length - 1;
	}

	/** Retourne la dernière Rule (appelé au début de tryXxx() et calcule aussi ctx.skRuleStack). */
	export function ctxLastSkRule(ctx: OSkImportCtxInternal): SkRule {
		if (ctx.skRuleStack !== undefined) return ctx.skRuleStack ? ctx.skRuleStack[ctx.skRuleStack.length - 1] : ctx.skNode.rule;
		//build skRuleStack.
		const skNode = ctx.skNode;
		ctx.skRuleStack = null;
		if (ctx.virtualPath) {
			let rule = ctx.skNode.rule;
			for (let i = 0; i < ctx.virtualPath.length; i++) {
				const part = ctx.virtualPath[i];
				if (typeof part === 'object') {
					if (!ctx.skRuleStack) ctx.skRuleStack = [];
					if (JML.jmlNode2nodeType(part) === ENodeType.element) {
						rule = rule.contentRule.findRuleNodeFor(part) || rule.contentRule.getUnknownNodeRule(skNode, part);
						ctx.skRuleStack.push(rule);
					} else {
						//comment
						ctx.skRuleStack.push(skNode.rule.getUnknownNodeRule(skNode, part));
					}
				} else if (part === '@') {
					if (!ctx.skRuleStack) ctx.skRuleStack = [];
					const xa = ctx.xaParent || ctx.sel.start;
					const idx = xa.length - (ctx.virtualPath.length - i);
					let attName = xa[idx];
					if (typeof attName !== 'string') attName = xa[idx - 1]; //cas du path virtual att/0
					if (typeof attName !== 'string') throw Error("Incoherent xa (" + xa + ") and virtualPath (" + ctx.virtualPath + ").");
					ctx.skRuleStack.push(rule.contentRule.findRuleAttrFor(attName) || rule.contentRule.getUnknownAttRule(skNode, attName));
				}
				//else if (part === '#') noeud text terminal, pas de skMeta associé.
			}
			if (ctx.sel && XA.isAttribute(ctx.sel.start)) {//pas si OSkPasteSearchContext (ctx.xaParent), déjà traité dans ctx.virtualPath
				//Le point d'insertion terminal de la sel est un attribut
				const attName = XA.last(ctx.xaParent || ctx.sel.start) as string;
				ctx.skRuleStack.push(rule.contentRule.findRuleAttrFor(attName) || rule.contentRule.getUnknownAttRule(skNode, attName));
			}
		} else if (XA.isAttribute(ctx.xaParent || ctx.sel.start)) {
			//Si on pointe un attr et non un offset...
			const attName = XA.last(ctx.xaParent || ctx.sel.start) as string;
			const rule = skNode.rule;
			ctx.skRuleStack = [rule.contentRule.findRuleAttrFor(attName) || rule.contentRule.getUnknownAttRule(skNode, attName)];
		}
		return ctx.skRuleStack ? ctx.skRuleStack[ctx.skRuleStack.length - 1] : skNode.rule;
	}

	/** */
	export function ctxFindSkRuleInAnc(ctx: OSkImportCtxInternal, filter: (r: SkRule, n?: SkNode) => boolean): SkRule {
		if (ctx.skRuleStack) for (let i = ctx.skRuleStack.length - 1; i >= 0; i--) {
			if (filter(ctx.skRuleStack[i])) return ctx.skRuleStack[i];
		}
		if (filter(ctx.skNode.rule, ctx.skNode)) return ctx.skNode.rule;
		for (let p = ctx.skNode.node.parentNode; p; p = p.parentNode) {
			const skP = ctx.schemaDom.getSkNode(p);
			if (skP && filter(skP.rule, skP)) return skP.rule;
		}
		return null;
	}

	export function doPasteReplaceSel(content: IJmlSet | string, ctx: OSkImportCtxInternal, schemaDom: SchemaDom, batch: XmlBatch) {
		content = buildJmlFromVirtualPath(ctx.sel.start, ctx.virtualPath, content);
		const simpleStr = typeof content === 'string' ? content : (content.length === 1 && typeof content[0] === 'string') ? content[0] as string : null;
		if (simpleStr) {
			//on insère une simple string
			if (!XA.isCollapsed(ctx.sel)) schemaDom.exportRange({start: ctx.sel.start, end: ctx.sel.end, deletes: batch}, null);
			const startOffset = SKMETA.ctxInsertStartOffset(ctx);
			if (typeof startOffset === 'string') {
				//affectation d'un attribut (remplacement atomique)
				batch.add(new XmlStrMsg().init(XA.isAttribute(ctx.sel.start) ? ctx.sel.start : XA.up(ctx.sel.start), simpleStr));
			} else if (ctx.ctn instanceof CharacterData || ctx.ctn instanceof Attr) {
				//insertion dans un noeud text/comment/attribut
				batch.add(new XmlInsertMsg().init(ctx.sel.start, simpleStr));
				batch.setSelAfterSeq(ctx.sel.start, simpleStr.length);
			} else {
				const xaInsert = ctx.sel.start.slice(0, ctx.insertDepth + 1);
				const beforeNode = ctx.ctn.childNodes.item(startOffset);
				if (beforeNode instanceof Text) {
					//Le précédent est un text, on append dans ce texte.
					batch.add(new XmlInsertMsg().init(xaInsert, simpleStr));
					batch.setSelAfterSeq(xaInsert, simpleStr.length);
				} else {
					const afterNode = ctx.ctn.childNodes.item(SKMETA.ctxInsertEndOffset(ctx) as number/* si string alors on aurait eu typeof ctx.insertPoint==='string'*/);
					if (afterNode instanceof Text) {
						//Le suivant est un text, on preppend dans ce noeud texte.
						const start = XA.append(XA.incrAtDepth(xaInsert, -1, 1), 0);
						batch.add(new XmlInsertMsg().init(start, simpleStr));
						batch.setSelAfterSeq(start, simpleStr.length);
					} else {
						//On ajoute un noeud texte.
						batch.add(new XmlInsertMsg().init(xaInsert, [simpleStr]));
						batch.setSelAfterSeq(xaInsert, 1);
					}
				}
			}
		} else {
			//on insère une struct plus complexe
			let startSelXa = ctx.sel.start;
			let endSelXa = ctx.sel.end || ctx.sel.start;
			if (!ctx.virtualPath) {
				//Même si sel collapsed pour forceSplitText
				schemaDom.exportRange({start: startSelXa, end: ctx.sel.end, deletes: batch, forceSplitText: true}, null);
			}
			let xaInsert = startSelXa.slice(0, ctx.insertDepth + 1);
			if (ctx.ctn instanceof Text) {
				//le text a été splitté
				const first = content[0];
				const last = content[content.length - 1];
				//on se place après le 1er noeud texte.
				xaInsert = XA.freeze(XA.incrAtDepth(XA.up(xaInsert), -1, 1));
				let selAfterStartXa: IXAddr;
				if (typeof first === "string") {
					//on fusionne Les 2 noeuds texte de début.
					batch.add(new XmlInsertMsg().init(startSelXa, first));
					(content as IJmlSet).splice(0, 1);
					selAfterStartXa = startSelXa;
				}
				let selAfterEndXa: IXAddr;
				if (typeof last === "string") {
					//On cherche le noeud texte de fin dans lequel on va devoir fusionner
					let textAfterXa: IXAddr;
					if (XA.isInSameSeq(startSelXa, endSelXa)) {
						//Même noeud texte, on a un noeud texte after si on n'est pas à la fin.
						textAfterXa = XA.last(endSelXa) < ctx.ctn.length ? xaInsert : null;
					} else if (startSelXa.length === endSelXa.length && XA.findForkDepth(startSelXa, endSelXa) === startSelXa.length - 2) {
						//2 noeuds textes dans la même séquence.
						textAfterXa = xaInsert;
					}
					if (textAfterXa) {
						//on fusionne Les 2 noeuds texte de fin.
						batch.add(new XmlInsertMsg().init(XA.append(textAfterXa, 0), last));
						(content as IJmlSet).length = (content as IJmlSet).length - 1;
						selAfterEndXa = XA.append(XA.incrAtDepth(textAfterXa, -1, JML.lengthJmlOrText(content)), last.length);
					}
				}
				batch.add(new XmlInsertMsg().init(xaInsert, content));
				if (selAfterEndXa) {
					batch.setSelAfter(selAfterStartXa || xaInsert, selAfterEndXa);
				} else if (selAfterStartXa) {
					batch.setSelAfter(selAfterStartXa, XA.incrAtDepth(xaInsert, -1, JML.lengthJmlOrText(content)));
				} else {
					batch.setSelAfterSeq(xaInsert, JML.lengthJmlOrText(content));
				}
			} else {
				if (ctx.insertDepth < SKMETA.ctxRealStartDepth(ctx)) {
					//insertion dans un container ancêtre, on se place après le noeud partiellement coupé.
					xaInsert = XA.incrAtDepth(xaInsert, -1, 1);
				}
				batch.add(new XmlInsertMsg().init(xaInsert, content));
				batch.setSelAfterSeq(xaInsert, JML.lengthJmlOrText(content));
			}
		}
	}

	export function replaceLabelOnImports(imps: ISkImporter[], label: string): ISkImporter[] {
		class SkImpLabelWrapper extends SkImpWrapper {
			constructor(subImporter: ISkImporter, public label: string) {super(null, subImporter, subImporter.malus)}

			getLabel(): string {return this.label}
		}

		if (imps.length > 1) return imps.map((s) => {
			const subLabel = s.getLabel();
			return new SkImpLabelWrapper(s, subLabel ? label + " + " + subLabel : label);
		});
		return [new SkImpLabelWrapper(imps[0], label)];
	}

	/** Retourne la balise sc:fragment/sc:attributes (ou sc:attributes) pour le presse-papier d'attributs. */
	export function getAttsInFragment(content: Node): Element | null {
		if (content.nodeName === DOM.SCFRAGMENT_TAG && content.namespaceURI === DOM.SCCORE_NS) {
			return DOM.findFirstChild(content, (ch): ch is Element => ch.nodeName === SCATTRIBUTES_TAG && ch.namespaceURI === DOM.SCCORE_NS);
		} else if (content.nodeName === SCATTRIBUTES_TAG && content.namespaceURI === DOM.SCCORE_NS) {
			return content as Element;
		}
		return null;
	}

	export const SCATTRIBUTES_TAG = 'sc:attributes'; //copie d'attributs dans le presse-papier, fils de sc:fragment
}


/** Execution standard de l'import en fils du container de la sélection de départ. */
function buildJmlFromVirtualPath(insertPoint: IXAddr, virtualPath: Array<IJmlObj | '#' | '@'>, content: IJmlSet | string): IJmlSet | string {
	if (virtualPath) {
		let i = virtualPath.length - 1;
		if (XA.isAttribute(insertPoint)) {
			//Le point d'insertion final n'est pas un offset mais un attr
			if (Array.isArray(content)) throw Error("Content can not be a node set.");
			const prevPart = virtualPath[i--] as IJmlObj;
			prevPart[XA.last(insertPoint) as string] = content;
			content = [prevPart];
		}
		for (; i >= 0; i--) {
			const part = virtualPath[i];
			if (part === '#') {
				//Contextes possibles :
				// insertPoint = 0/2/0/0, avec 0/0 en noeuds virtuels, et virtualPath = ['#']
				// insertPoint = 0/2/0/0/0, avec 0/0/0 en noeuds virtuels, et virtualPath = [{'':'tag'}, '#']
				if (typeof content === 'string') content = [content];
			} else if (part === '@') {
				if (Array.isArray(content)) throw Error("Content can not be a node set.");
				//Contextes possibles :
				//insertPoint = 0/2/att/0, avec att/0 en noeuds virtuels, et virtualPath = ['@']
				if (i === 0) return content; // rien à faire ici.
				//insertPoint = 0/2/0/att/0, avec 0/att/0 en noeuds virtuels, et virtualPath = [{'':'tag'}, '@']
				const prevPart = virtualPath[--i] as IJmlObj;
				if (XA.isAttribute(insertPoint)) {
					//insertPoint = 0/2/0/att
					prevPart[XA.last(insertPoint) as string] = content;
				} else {
					//insertPoint = 0/2/0/att/0
					prevPart[insertPoint[insertPoint.length - 2] as string] = content;
				}
				content = [prevPart];
			} else {
				content = [part, Array.isArray(content) ? content : [content]];
			}
		}
	}
	return content;
}


/** Racine d'une liste d'éléments optinonnellement contrainte en cardinalité. */
class SkMBounded extends SkMObject implements ISkMetaAsList {

	get asGramList(): "list" | "entry" {return 'list'}

	initSkRule(skRule: SkRuleNode & ISkMBoundedProps, confRule: Element): void {
		skRule.cardMinError = parseInt(confRule.getAttributeNS(SK_NS, 'cardMinError')) || 0;
		skRule.cardMaxError = parseInt(confRule.getAttributeNS(SK_NS, 'cardMaxError')) || Infinity;
		skRule.cardTagName = confRule.getAttributeNS(SK_NS, 'cardTagName');
	}

	onExecRules(skCtx: SkContext, rule: ISkRuleObj & ISkMBoundedProps, node: Element): void {
		if (skCtx.execOptions.genAnnots || skCtx.execOptions.autoComplete) {
			const min = rule.cardMinError;
			const max = rule.cardMaxError;
			let count = 0;
			let lastCh: Element;
			for (let ch = node.firstElementChild; ch; ch = ch.nextElementSibling) {
				if (!rule.cardTagName || ch.nodeName === rule.cardTagName) {
					count++;
					if (count > max && skCtx.execOptions.genAnnots) {
						skCtx.addAnnot(new SkAnnotEltFree().init(ch, `Un maximum de ${max} items est autorisé.`));
					}
					lastCh = ch;
				}
			}
			if (count < min) {
				if (skCtx.execOptions.autoComplete && lastCh) {
					//Le fils à controler DOIT être en card +,
					//l'injection d'un 1er élément va donc être assuré par le RNG,
					//on ne s'occupe que d'injecter les suivants
					const toInsert = [] as IJmlSet;
					for (; count < min; count++) {
						//on reprend la rule de lastChild qu'on réplique
						skCtx.skNode.schemaDom.getSkNode(lastCh).rule.createContent(toInsert);
					}
					//et qu'on insert après.
					skCtx.execOptions.corrections.push(new XmlInsertMsg().init(XA.incrAtDepth(XA.from(lastCh), -1, 1), toInsert));
				} else if (min === 1) {
					skCtx.addAnnot(new SkAnnotEltFree().init(node, `Cet élément doit être complété.`));
				} else {
					skCtx.addAnnot(new SkAnnotEltFree().init(node, `Un minimum de ${min} items est requis.`));
				}
			}
		}
	}

	// computeGramBindingMalus(gram: IGram, rule: ISkRuleObj, childrenMalus: number): number {
	// 	return super.computeGramBindingMalus(gram, rule, childrenMalus);
	// }
}

type ISkMBoundedProps = {
	cardMinError?: number
	cardMaxError?: number
	cardTagName?: string
}

SKMETALIB.registerMetaNode(new SkMBounded('BoundedList'));


/** Entrées d'une liste d'éléments optinonnellement contrainte en cardinalité. */
class SkMBoundedEntry extends SkMObject implements ISkMetaAsList {

	get asGramList(): "list" | "entry" {return 'entry'}

	computeGramBindingMalus(gram: IGram, rule: ISkRuleObj, childrenMalus: number): number {
		//Si le gram n'est pas un li : on pénalise fortement car on tente de traverser cet élément target, alors qu'on devrait construire ici une liste.
		if (gram.htmlTag !== 'li') return (10 * gram.weight) + childrenMalus;
		return .1 * childrenMalus; //on réduit le malus des fils pour privilégier cette option
	}
}

SKMETALIB.registerMetaNode(new SkMBoundedEntry('BoundedListEntry'));
